import { useMemo, useState } from "react"

import { ANNOTATION_VALUE } from "@/constants/annotation-values"
import {
  getMetricColor,
  hasBinaryUnknowns,
  type YesNoLabels,
} from "@/metrics/metric-display"
import { Checkbox } from "@coval/ui/checkbox"

import {
  barSeriesColor,
  chartBarSeriesColors,
} from "@/components/charts/seriesPalette"
import type { ChartBarFilter } from "@/types/ChartBarFilter"
import type { TargetCondition } from "@/types/metrics/contracts"
import type { RunMetricOutput } from "@/types/RunMetricOutput"
import { formatMetricDisplayName } from "@/utils/formatting/format-metric-display-results"

import {
  HorizontalStackedBarChart,
  type HorizontalBarSegment,
} from "../charts/HorizontalStackedBarChart"

// Pass / fail bar fills use the Coval Accessible Data Palette's green / red-orange,
// so the result distribution reads on-palette. Theme-aware bar tones (mid in
// light, strong in dark). Intentionally decoupled from the Yes/No chip surface
// tokens. The neutral `default` (e.g. the binary "unknown" bar) uses the
// theme-aware warm-grey surface token. Keys match getMetricColor's return type.
const CHART_COLORS: Record<ReturnType<typeof getMetricColor>, string> = {
  success: barSeriesColor("green"),
  danger: barSeriesColor("red-orange"),
  default: "var(--surface-2)",
}

export const AggregateOverviewString = ({
  mos,
  isBinary = false,
  targetCondition,
  includeUnknowns: controlledIncludeUnknowns,
  onIncludeUnknownsChange,
  hideCheckbox = false,
  onBarClick,
  activeFilters,
  binaryLabels,
  binaryColors,
}: {
  mos: RunMetricOutput[]
  isBinary?: boolean
  targetCondition?: TargetCondition
  /** Controlled mode: current value of include unknowns toggle */
  includeUnknowns?: boolean
  /** Controlled mode: callback when toggle changes */
  onIncludeUnknownsChange?: (value: boolean) => void
  /** When true, don't render the checkbox (parent handles it) */
  hideCheckbox?: boolean
  onBarClick?: (filter: { type: "exact"; value: string }) => void
  activeFilters?: ChartBarFilter[]
  /** Override the YES/NO/UNKNOWN bar labels (e.g. Match/Diff/Not Found). */
  binaryLabels?: YesNoLabels
  binaryColors?: Record<string, ReturnType<typeof getMetricColor>>
}) => {
  const [internalIncludeUnknowns, setInternalIncludeUnknowns] = useState(true)

  // Use controlled value if provided, otherwise use internal state
  const includeUnknowns = controlledIncludeUnknowns ?? internalIncludeUnknowns
  const setIncludeUnknowns =
    onIncludeUnknownsChange ?? setInternalIncludeUnknowns

  // First, determine if there are unknowns (needed for showing the toggle)
  const hasUnknowns = useMemo(() => {
    if (!isBinary) {
      return false
    }
    return hasBinaryUnknowns(mos)
  }, [mos, isBinary])

  const segments = useMemo((): HorizontalBarSegment[] => {
    const count = mos.filter((m) => m.string_value !== null).length
    if (count === 0) {
      return []
    }

    // Get counts of each string value
    const valueCounts = mos.reduce((acc: Record<string, number>, curr) => {
      if (curr.string_value) {
        const normalizedValue = curr.string_value.trim()
        acc[normalizedValue] = (acc[normalizedValue] || 0) + 1
      }
      return acc
    }, {})

    // Check if we have the special YES/NO/UNKNOWN case
    let topValues: [string, number][]
    if (isBinary) {
      // Special case: render Yes, No, Unknown in that order
      // Always include all three categories even if count is 0
      const orderedValues = [
        ANNOTATION_VALUE.YES,
        ANNOTATION_VALUE.NO,
        ANNOTATION_VALUE.UNKNOWN,
      ]
      topValues = orderedValues.map(
        (key) => [key, valueCounts[key] || 0] as [string, number],
      )
    } else {
      // Sort alphabetically by string value and take top 4
      topValues = Object.entries(valueCounts)
        .sort(([a], [b]) => a.localeCompare(b))
        .slice(0, 4)
    }

    // For binary metrics, calculate percentages based on includeUnknowns setting
    const percentageTotal = isBinary
      ? includeUnknowns
        ? (valueCounts[ANNOTATION_VALUE.YES] || 0) +
          (valueCounts[ANNOTATION_VALUE.NO] || 0) +
          (valueCounts[ANNOTATION_VALUE.UNKNOWN] || 0)
        : (valueCounts[ANNOTATION_VALUE.YES] || 0) +
          (valueCounts[ANNOTATION_VALUE.NO] || 0)
      : topValues.reduce((sum, [, c]) => sum + c, 0)

    // Non pass/fail categories use the accessible series palette (distinct
    // identity hues); the binary Yes/No/Unknown path stays on the semantic
    // pass/fail tokens.
    const categoricalColors = chartBarSeriesColors(topValues.length)

    // Map to segments with colors
    return topValues.map(([value, count], i) => {
      let color: string
      if (isBinary) {
        const colorKey =
          binaryColors?.[value] ?? getMetricColor(value, targetCondition)
        color = CHART_COLORS[colorKey]
      } else {
        color = categoricalColors[i] ?? CHART_COLORS.default
      }

      // Format label with proper casing for display (never show "Unknown" in metric results)
      let displayLabel: string
      if (isBinary && binaryLabels) {
        displayLabel =
          value === ANNOTATION_VALUE.YES
            ? (binaryLabels.yes ?? "Yes")
            : value === ANNOTATION_VALUE.NO
              ? (binaryLabels.no ?? "No")
              : value === ANNOTATION_VALUE.UNKNOWN
                ? (binaryLabels.unknown ?? "N/A")
                : formatMetricDisplayName(value)
      } else {
        displayLabel =
          isBinary && value === ANNOTATION_VALUE.UNKNOWN
            ? "N/A"
            : formatMetricDisplayName(value)
      }

      // Calculate percentage based on includeUnknowns setting
      const percent =
        percentageTotal > 0 ? ((count / percentageTotal) * 100).toFixed(0) : "0"

      // Mark Unknown bar as striped when unknowns are excluded
      const striped =
        isBinary && value === ANNOTATION_VALUE.UNKNOWN && !includeUnknowns

      return {
        label: displayLabel,
        rawValue: value,
        value: count,
        color,
        percent,
        striped,
      }
    })
  }, [
    mos,
    isBinary,
    targetCondition,
    includeUnknowns,
    binaryLabels,
    binaryColors,
  ])

  if (segments.length === 0) {
    return null
  }

  return (
    <div>
      {isBinary && hasUnknowns && !hideCheckbox && (
        <label className="mb-1 flex items-center gap-1.5 cursor-pointer">
          <Checkbox
            checked={includeUnknowns}
            onCheckedChange={(checked) => setIncludeUnknowns(!!checked)}
            aria-label="Include Unknowns in total"
          />
          <span className="text-[10px] text-coval-text-muted">
            Include Unknowns in total
          </span>
        </label>
      )}
      <HorizontalStackedBarChart
        segments={segments}
        onBarClick={onBarClick}
        activeFilters={activeFilters}
      />
    </div>
  )
}
